import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramAwards = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [awardsData, setAwardsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // const { selectedResult } = useAppContext(); // Removed context usage

  // Fetch awards data (wrapped in useCallback)
  const fetchAwards = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getAwards(programId); // Use programId prop
      setAwardsData(data || []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch awards data:', err);
      setError(t('programAwards.error'));
      setAwardsData([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchAwards();
  }, [fetchAwards]);

  // Function to render winning status
  const renderWinStatus = (won) => {
    if (won === 1) {
      return <span className="font-medium text-green-600">{t('programAwards.status.won')}</span>;
    } else if (won === 0) {
      return <span className="text-gray-500">{t('programAwards.status.nominated')}</span>;
    } else {
      return <span></span>; // Leave blank if status is unknown
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('programAwards.loading')}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{t('programAwards.error')}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchAwards} // Retry fetch
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!awardsData || awardsData.length === 0) { // Check if awardsData is null or empty
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programAwards.noAwards')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto">
      <div className="min-w-full">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left sticky top-0">
            <tr>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAwards.columns.year')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAwards.columns.award')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAwards.columns.category')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAwards.columns.result')}</th>
              <th className="px-4 py-2 font-medium text-gray-600">{t('programAwards.columns.recipient')}</th>
            </tr>
          </thead>
          <tbody>
            {awardsData.map((item, index) => (
              <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                <td className="px-4 py-3">{item.year || '-'}</td>
                <td className="px-4 py-3">{item.award || '-'}</td>
                <td className="px-4 py-3">{item.category || '-'}</td>
                <td className="px-4 py-3">
                  {renderWinStatus(item.won)}
                </td>
                <td className="px-4 py-3">{item.recipient || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
          <div>
            {t('programAwards.showing', { count: awardsData.length })}
          </div>
          <div>
            <button
              className={`px-3 py-1 border border-gray-300 rounded-sm ${awardsData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              onClick={() => exportToCsv(awardsData, `${programId}_awards.csv`)}
              disabled={awardsData.length === 0}
            >
              {t('programAwards.export')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramAwards;
